/**
 * Notification-related React Query hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { log } from '../utils/logger';
import { notificationAPI } from '../api/notifications';
import { queryKeys } from './queryKeys';
import { toast } from 'react-hot-toast';
import { supabase } from '../lib/supabase';

/**
 * Hook to fetch all notifications for the current user
 * @param {Object} options - Query options
 */
export const useNotifications = (options = {}) => {
  const { includeArchived = false, limit = 50, offset = 0 } = options;

  return useQuery({
    queryKey: queryKeys.notifications.list({ includeArchived, limit, offset }),
    queryFn: () => notificationAPI.getNotifications({ includeArchived, limit, offset }),
    staleTime: 1000 * 30, // 30 seconds
    cacheTime: 1000 * 60 * 5, // 5 minutes
    refetchInterval: 1000 * 60, // Refetch every minute for fresh notifications
  });
};

/**
 * Hook to get unread notification count
 */
export const useUnreadNotificationCount = () => {
  return useQuery({
    queryKey: queryKeys.notifications.unreadCount(),
    queryFn: () => notificationAPI.getUnreadCount(),
    staleTime: 1000 * 30, // 30 seconds
    cacheTime: 1000 * 60 * 5, // 5 minutes
    refetchInterval: 1000 * 60, // Refetch every minute
  });
};

/**
 * Hook to mark a notification as read
 */
export const useMarkAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId) => notificationAPI.markAsRead(notificationId),
    onSuccess: () => {
      // Invalidate notifications list and unread count
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
      log.info('✅ Notification marked as read');
    },
    onError: (error) => {
      log.error('Failed to mark notification as read:', error);
      toast.error('Failed to mark notification as read');
    }
  });
};

/**
 * Hook to mark multiple notifications as read
 */
export const useMarkMultipleAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationIds) => notificationAPI.markMultipleAsRead(notificationIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
      log.info('✅ Notifications marked as read');
    },
    onError: (error) => {
      log.error('Failed to mark notifications as read:', error);
      toast.error('Failed to mark notifications as read');
    }
  });
};

/**
 * Hook to archive a notification
 */
export const useArchiveNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId) => notificationAPI.archiveNotification(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
      toast.success('Notification archived');
      log.info('✅ Notification archived');
    },
    onError: (error) => {
      log.error('Failed to archive notification:', error);
      toast.error('Failed to archive notification');
    }
  });
};

/**
 * Hook to create a notification
 */
export const useCreateNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationData) => notificationAPI.createNotification(notificationData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
      log.info('✅ Notification created');
    },
    onError: (error) => {
      log.error('Failed to create notification:', error);
      toast.error('Failed to create notification');
    }
  });
};

/**
 * Hook to mark all notifications as read
 */
export const useMarkAllAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notificationAPI.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
      toast.success('All notifications marked as read');
      log.info('✅ All notifications marked as read');
    },
    onError: (error) => {
      log.error('Failed to mark all as read:', error);
      toast.error('Failed to mark all as read');
    }
  });
};

/**
 * Hook for real-time notification updates
 * @param {Function} onNotificationChange - Callback for notification changes
 */
export const useNotificationSubscription = (onNotificationChange) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    let subscription;

    const setupSubscription = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        subscription = supabase
          .channel('notifications')
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'notifications',
              filter: `user_id=eq.${user.id}`
            },
            (payload) => {
              log.info('🔔 Notification change:', payload);
              
              // Invalidate queries to refresh data
              queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
              
              // Call the callback if provided
              if (onNotificationChange) {
                onNotificationChange(payload);
              }

              // Show toast for new notifications
              if (payload.eventType === 'INSERT') {
                toast('New notification received!', {
                  icon: '🔔',
                  duration: 4000,
                });
              }
            }
          )
          .subscribe();
      }
    };

    setupSubscription();

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [queryClient, onNotificationChange]);
};